import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FileCode, ArrowLeft, HelpCircle } from 'lucide-react';
import { useLanguage } from '../../i18n';

/**
 * Go结构体工具帮助文档页面
 */
function GoStructHelp() {
  const { language } = useLanguage();
  const [activeSection, setActiveSection] = useState(0);

  // 监听滚动，高亮当前章节
  useEffect(() => {
    const handleScroll = () => {
      const sections = document.querySelectorAll('section[id^="section-"]');
      let currentSection = 0;

      sections.forEach((section, index) => {
        const rect = section.getBoundingClientRect();
        if (rect.top <= 100 && rect.bottom > 100) {
          currentSection = index;
        }
      });

      setActiveSection(currentSection);
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // 初始调用

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const content = {
    zh: {
      title: 'Go结构体 ↔ JSON 转换工具使用指南',
      subtitle: '快速掌握DevToolkit的Go结构体转换工具',
      sections: [
        {
          title: '功能特性',
          items: [
            {
              title: 'JSON转Go结构体',
              desc: '自动从JSON数据生成Go语言结构体定义，支持嵌套对象和数组，智能识别数据类型。'
            },
            {
              title: 'Go结构体转JSON',
              desc: '将Go结构体定义转换为JSON示例数据，方便测试和文档编写。'
            },
            {
              title: '智能类型推断',
              desc: '自动识别JSON中的数据类型（string、int、float、bool等），生成精确的Go类型定义。'
            },
            {
              title: '自定义标签',
              desc: '支持生成json、xml、yaml等标签，可以自定义标签格式和命名规则。'
            },
            {
              title: '驼峰命名转换',
              desc: '自动将snake_case命名转换为Go风格的PascalCase，符合Go语言规范。'
            },
            {
              title: '嵌套结构支持',
              desc: '完整支持嵌套对象和数组，自动生成子结构体，保持层级关系清晰。'
            }
          ]
        },
        {
          title: '使用教程',
          content: `
            <h3>1. JSON转Go结构体</h3>
            <p><strong>步骤 1：输入JSON数据</strong></p>
            <p>在左侧编辑器中粘贴您的JSON数据。支持任何有效的JSON格式，包括嵌套对象和数组。</p>

            <p><strong>步骤 2：设置选项</strong></p>
            <ul>
              <li><strong>结构体名称：</strong>设置根结构体的名称（默认为 Response）</li>
              <li><strong>包名：</strong>设置Go包名（默认为 main）</li>
              <li><strong>标签类型：</strong>选择需要生成的标签（json、xml、yaml等）</li>
              <li><strong>命名风格：</strong>选择字段命名规则（PascalCase、camelCase等）</li>
            </ul>

            <p><strong>步骤 3：生成结构体</strong></p>
            <p>点击"转换"按钮，工具会自动生成Go结构体代码。右侧会显示完整的结构体定义，可以直接复制使用。</p>

            <p><strong>步骤 4：复制代码</strong></p>
            <p>点击"复制"按钮复制生成的Go代码，直接粘贴到您的项目中。</p>

            <h3>2. Go结构体转JSON</h3>
            <p><strong>步骤 1：输入Go代码</strong></p>
            <p>在左侧编辑器中粘贴您的Go结构体定义。支持多个结构体，工具会自动识别。</p>

            <p><strong>步骤 2：选择根结构体</strong></p>
            <p>如果有多个结构体，选择要转换的根结构体。</p>

            <p><strong>步骤 3：生成JSON</strong></p>
            <p>点击"转换"按钮，工具会生成JSON示例数据。右侧会显示格式化的JSON，可以直接使用。</p>

            <h3>3. 类型推断规则</h3>
            <p><strong>基本类型：</strong></p>
            <table>
              <thead>
                <tr>
                  <th>JSON类型</th>
                  <th>Go类型</th>
                  <th>示例</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>string</td>
                  <td>string</td>
                  <td>"hello"</td>
                </tr>
                <tr>
                  <td>number (整数)</td>
                  <td>int</td>
                  <td>123</td>
                </tr>
                <tr>
                  <td>number (小数)</td>
                  <td>float64</td>
                  <td>123.45</td>
                </tr>
                <tr>
                  <td>boolean</td>
                  <td>bool</td>
                  <td>true / false</td>
                </tr>
                <tr>
                  <td>null</td>
                  <td>interface{}</td>
                  <td>null</td>
                </tr>
                <tr>
                  <td>array</td>
                  <td>[]Type</td>
                  <td>[1, 2, 3]</td>
                </tr>
                <tr>
                  <td>object</td>
                  <td>struct</td>
                  <td>{"key": "value"}</td>
                </tr>
              </tbody>
            </table>

            <p><strong>特殊处理：</strong></p>
            <ul>
              <li>空数组会被推断为 <code>[]interface{}</code></li>
              <li>混合类型的数组会使用 <code>[]interface{}</code></li>
              <li>嵌套对象会自动生成子结构体</li>
              <li>数组中的对象会提取为单独的结构体类型</li>
            </ul>

            <h3>4. 标签定制</h3>
            <p><strong>支持的标签类型：</strong></p>
            <ul>
              <li><code>json</code> - JSON序列化标签（最常用）</li>
              <li><code>xml</code> - XML序列化标签</li>
              <li><code>yaml</code> - YAML序列化标签</li>
              <li><code>bson</code> - MongoDB BSON标签</li>
              <li><code>db</code> - 数据库字段标签</li>
              <li><code>form</code> - 表单字段标签</li>
            </ul>

            <p><strong>标签示例：</strong></p>
            <pre><code>type User struct {
    Name  string \`json:"name" xml:"name" yaml:"name"\`
    Age   int    \`json:"age" xml:"age" yaml:"age"\`
    Email string \`json:"email" xml:"email" yaml:"email"\`
}</code></pre>

            <h3>5. 命名规则</h3>
            <p><strong>字段命名转换：</strong></p>
            <ul>
              <li><code>user_name</code> → <code>UserName</code> (PascalCase，默认)</li>
              <li><code>user_name</code> → <code>userName</code> (camelCase)</li>
              <li><code>username</code> → <code>Username</code> (首字母大写)</li>
            </ul>

            <p><strong>结构体命名：</strong></p>
            <ul>
              <li>嵌套对象根据键名生成结构体名称</li>
              <li>数组元素对象会添加Item后缀</li>
              <li>避免命名冲突，自动添加数字后缀</li>
            </ul>
          `
        },
        {
          title: '常见问题',
          faqs: [
            {
              q: '生成的结构体可以直接使用吗？',
              a: '可以。生成的代码符合Go语言规范，可以直接复制到项目中使用。不过建议根据实际需求调整字段名称、类型和注释。'
            },
            {
              q: '如何处理可选字段？',
              a: '对于可能为null的字段，建议手动修改为指针类型。例如将 int 改为 *int，这样可以区分零值和null值。工具默认不使用指针类型。'
            },
            {
              q: '嵌套层级太深怎么办？',
              a: '工具支持任意深度的嵌套。不过如果结构过于复杂，建议手动拆分为多个独立的结构体，这样更易维护。'
            },
            {
              q: '数组类型推断不准确怎么办？',
              a: '如果数组为空或混合类型，工具会使用 []interface{}。建议手动修改为正确的类型。如果数组有多个对象，工具会根据第一个对象推断类型。'
            },
            {
              q: '如何生成omitempty标签？',
              a: '在标签选项中可以启用omitempty。这样字段为零值时，序列化为JSON时会自动省略该字段。例如：json:"name,omitempty"'
            },
            {
              q: 'Go结构体转JSON时如何处理嵌套？',
              a: '工具会递归处理所有嵌套结构体，生成完整的JSON示例数据。每个字段都会填充对应类型的示例值。'
            },
            {
              q: '支持泛型吗？',
              a: '目前不支持泛型。生成的代码使用传统的具体类型。如果需要泛型，需要手动修改生成的代码。'
            },
            {
              q: '字段注释会保留吗？',
              a: 'Go转JSON时会尝试保留注释信息。JSON转Go时建议手动添加注释，说明每个字段的用途。'
            },
            {
              q: '如何处理时间字段？',
              a: '时间字符串会被识别为string类型。建议手动修改为time.Time类型，并添加合适的序列化标签。'
            },
            {
              q: '私有字段会被导出吗？',
              a: '所有生成的字段都是公开的（首字母大写），这样才能被JSON包序列化。如果需要私有字段，需要手动修改。'
            }
          ]
        },
        {
          title: '使用场景',
          content: `
            <h3>1. API开发</h3>
            <ul>
              <li>快速从API文档的JSON示例生成Go结构体</li>
              <li>为API响应创建类型定义</li>
              <li>生成请求体的结构体定义</li>
              <li>创建数据传输对象(DTO)</li>
            </ul>

            <h3>2. 数据库模型</h3>
            <ul>
              <li>从JSON配置生成数据库表结构</li>
              <li>创建ORM模型定义</li>
              <li>设计数据库Schema</li>
              <li>生成迁移脚本的数据结构</li>
            </ul>

            <h3>3. 配置文件</h3>
            <ul>
              <li>解析JSON配置文件</li>
              <li>生成配置结构体</li>
              <li>验证配置格式</li>
              <li>创建配置模板</li>
            </ul>

            <h3>4. 测试和文档</h3>
            <ul>
              <li>生成测试用的JSON数据</li>
              <li>创建API文档示例</li>
              <li>编写单元测试的mock数据</li>
              <li>生成Swagger文档的model</li>
            </ul>

            <h3>5. 数据转换</h3>
            <ul>
              <li>将第三方API返回转换为Go类型</li>
              <li>统一不同数据源的格式</li>
              <li>重构旧代码的数据结构</li>
              <li>迁移项目时的类型定义</li>
            </ul>
          `
        },
        {
          title: '最佳实践',
          content: `
            <h3>代码规范</h3>
            <ul>
              <li>结构体名称使用PascalCase（首字母大写的驼峰命名）</li>
              <li>字段名称也使用PascalCase，保证可导出</li>
              <li>添加有意义的注释说明每个字段的用途</li>
              <li>相关的结构体放在同一个文件中</li>
            </ul>

            <h3>类型选择</h3>
            <ul>
              <li>整数优先使用int，大数使用int64</li>
              <li>浮点数统一使用float64</li>
              <li>可选字段使用指针类型（*string、*int等）</li>
              <li>时间字段使用time.Time而不是string</li>
              <li>枚举值考虑使用自定义类型</li>
            </ul>

            <h3>标签使用</h3>
            <ul>
              <li>必填字段不使用omitempty</li>
              <li>可选字段添加omitempty标签</li>
              <li>与JSON键名不同时明确指定标签</li>
              <li>使用"-"标签忽略不需要序列化的字段</li>
            </ul>

            <h3>结构设计</h3>
            <ul>
              <li>避免过深的嵌套，超过3层考虑拆分</li>
              <li>公共的子结构体可以复用</li>
              <li>使用组合而不是继承</li>
              <li>大结构体拆分为多个小结构体</li>
            </ul>

            <h3>示例代码</h3>
            <pre><code>package models

import "time"

// User 用户信息
type User struct {
    ID        int64     \`json:"id"\`
    Name      string    \`json:"name"\`
    Email     string    \`json:"email"\`
    Age       *int      \`json:"age,omitempty"\`
    CreatedAt time.Time \`json:"created_at"\`
    Profile   *Profile  \`json:"profile,omitempty"\`
}

// Profile 用户资料
type Profile struct {
    Bio    string   \`json:"bio,omitempty"\`
    Avatar string   \`json:"avatar,omitempty"\`
    Tags   []string \`json:"tags,omitempty"\`
}</code></pre>
          `
        }
      ]
    },
    en: {
      title: 'Go Struct ↔ JSON Converter Guide',
      subtitle: 'Quickly master DevToolkit\'s Go struct converter',
      sections: [
        {
          title: 'Features',
          items: [
            {
              title: 'JSON to Go Struct',
              desc: 'Auto-generate Go struct definitions from JSON data, supports nested objects and arrays, intelligently recognizes data types.'
            },
            {
              title: 'Go Struct to JSON',
              desc: 'Convert Go struct definitions to JSON sample data, convenient for testing and documentation.'
            },
            {
              title: 'Smart Type Inference',
              desc: 'Auto-recognizes data types in JSON (string, int, float, bool, etc.), generates precise Go type definitions.'
            },
            {
              title: 'Custom Tags',
              desc: 'Supports generating json, xml, yaml tags, customizable tag formats and naming rules.'
            },
            {
              title: 'Camel Case Conversion',
              desc: 'Auto-converts snake_case naming to Go-style PascalCase, compliant with Go conventions.'
            },
            {
              title: 'Nested Structure Support',
              desc: 'Full support for nested objects and arrays, auto-generates sub-structs, maintains clear hierarchy.'
            }
          ]
        },
        {
          title: 'Tutorial',
          content: `
            <h3>1. JSON to Go Struct</h3>
            <p><strong>Step 1: Input JSON Data</strong></p>
            <p>Paste your JSON data in the left editor. Supports any valid JSON format including nested objects and arrays.</p>

            <p><strong>Step 2: Set Options</strong></p>
            <ul>
              <li><strong>Struct Name:</strong> Set root struct name (default: Response)</li>
              <li><strong>Package Name:</strong> Set Go package name (default: main)</li>
              <li><strong>Tag Types:</strong> Select tags to generate (json, xml, yaml, etc.)</li>
              <li><strong>Naming Style:</strong> Choose field naming convention (PascalCase, camelCase, etc.)</li>
            </ul>

            <p><strong>Step 3: Generate Struct</strong></p>
            <p>Click "Convert" button, tool auto-generates Go struct code. Right side shows complete struct definition, ready to copy.</p>

            <p><strong>Step 4: Copy Code</strong></p>
            <p>Click "Copy" button to copy generated Go code, paste directly into your project.</p>

            <h3>2. Go Struct to JSON</h3>
            <p><strong>Step 1: Input Go Code</strong></p>
            <p>Paste your Go struct definition in left editor. Supports multiple structs, tool auto-recognizes.</p>

            <p><strong>Step 2: Select Root Struct</strong></p>
            <p>If multiple structs, select root struct to convert.</p>

            <p><strong>Step 3: Generate JSON</strong></p>
            <p>Click "Convert" button, tool generates JSON sample data. Right side shows formatted JSON, ready to use.</p>

            <h3>3. Type Inference Rules</h3>
            <p><strong>Basic Types:</strong></p>
            <table>
              <thead>
                <tr>
                  <th>JSON Type</th>
                  <th>Go Type</th>
                  <th>Example</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>string</td>
                  <td>string</td>
                  <td>"hello"</td>
                </tr>
                <tr>
                  <td>number (integer)</td>
                  <td>int</td>
                  <td>123</td>
                </tr>
                <tr>
                  <td>number (decimal)</td>
                  <td>float64</td>
                  <td>123.45</td>
                </tr>
                <tr>
                  <td>boolean</td>
                  <td>bool</td>
                  <td>true / false</td>
                </tr>
                <tr>
                  <td>null</td>
                  <td>interface{}</td>
                  <td>null</td>
                </tr>
                <tr>
                  <td>array</td>
                  <td>[]Type</td>
                  <td>[1, 2, 3]</td>
                </tr>
                <tr>
                  <td>object</td>
                  <td>struct</td>
                  <td>{"key": "value"}</td>
                </tr>
              </tbody>
            </table>

            <p><strong>Special Handling:</strong></p>
            <ul>
              <li>Empty arrays inferred as <code>[]interface{}</code></li>
              <li>Mixed type arrays use <code>[]interface{}</code></li>
              <li>Nested objects auto-generate sub-structs</li>
              <li>Objects in arrays extracted as separate struct types</li>
            </ul>

            <h3>4. Tag Customization</h3>
            <p><strong>Supported Tag Types:</strong></p>
            <ul>
              <li><code>json</code> - JSON serialization tags (most common)</li>
              <li><code>xml</code> - XML serialization tags</li>
              <li><code>yaml</code> - YAML serialization tags</li>
              <li><code>bson</code> - MongoDB BSON tags</li>
              <li><code>db</code> - Database field tags</li>
              <li><code>form</code> - Form field tags</li>
            </ul>

            <p><strong>Tag Example:</strong></p>
            <pre><code>type User struct {
    Name  string \`json:"name" xml:"name" yaml:"name"\`
    Age   int    \`json:"age" xml:"age" yaml:"age"\`
    Email string \`json:"email" xml:"email" yaml:"email"\`
}</code></pre>

            <h3>5. Naming Rules</h3>
            <p><strong>Field Name Conversion:</strong></p>
            <ul>
              <li><code>user_name</code> → <code>UserName</code> (PascalCase, default)</li>
              <li><code>user_name</code> → <code>userName</code> (camelCase)</li>
              <li><code>username</code> → <code>Username</code> (capitalize first letter)</li>
            </ul>

            <p><strong>Struct Naming:</strong></p>
            <ul>
              <li>Nested objects generate struct names based on key names</li>
              <li>Array element objects add Item suffix</li>
              <li>Avoid naming conflicts, auto-add numeric suffix</li>
            </ul>
          `
        },
        {
          title: 'FAQ',
          faqs: [
            {
              q: 'Can generated structs be used directly?',
              a: 'Yes. Generated code complies with Go language specs and can be copied directly into projects. However, recommend adjusting field names, types, and comments based on actual needs.'
            },
            {
              q: 'How to handle optional fields?',
              a: 'For fields that may be null, recommend manually modifying to pointer types. For example, change int to *int to distinguish between zero values and null. Tool defaults to non-pointer types.'
            },
            {
              q: 'What about deeply nested levels?',
              a: 'Tool supports arbitrary nesting depth. However, if structure is too complex, recommend manually splitting into multiple independent structs for better maintainability.'
            },
            {
              q: 'What if array type inference is inaccurate?',
              a: 'If array is empty or mixed types, tool uses []interface{}. Recommend manually modifying to correct type. If array has multiple objects, tool infers type from first object.'
            },
            {
              q: 'How to generate omitempty tags?',
              a: 'Can enable omitempty in tag options. This way when field is zero value, it\'s automatically omitted during JSON serialization. Example: json:"name,omitempty"'
            },
            {
              q: 'How to handle nesting when converting Go struct to JSON?',
              a: 'Tool recursively processes all nested structs, generating complete JSON sample data. Each field is filled with sample values of corresponding type.'
            },
            {
              q: 'Are generics supported?',
              a: 'Currently not supported. Generated code uses traditional concrete types. If generics needed, must manually modify generated code.'
            },
            {
              q: 'Are field comments preserved?',
              a: 'Go to JSON tries to preserve comment information. JSON to Go recommend manually adding comments explaining each field\'s purpose.'
            },
            {
              q: 'How to handle time fields?',
              a: 'Time strings are recognized as string type. Recommend manually modifying to time.Time type and adding appropriate serialization tags.'
            },
            {
              q: 'Will private fields be exported?',
              a: 'All generated fields are public (capitalized first letter) so they can be serialized by JSON package. If private fields needed, must manually modify.'
            }
          ]
        },
        {
          title: 'Use Cases',
          content: `
            <h3>1. API Development</h3>
            <ul>
              <li>Quickly generate Go structs from API doc JSON examples</li>
              <li>Create type definitions for API responses</li>
              <li>Generate struct definitions for request bodies</li>
              <li>Create Data Transfer Objects (DTOs)</li>
            </ul>

            <h3>2. Database Models</h3>
            <ul>
              <li>Generate database table structures from JSON config</li>
              <li>Create ORM model definitions</li>
              <li>Design database schemas</li>
              <li>Generate data structures for migration scripts</li>
            </ul>

            <h3>3. Configuration Files</h3>
            <ul>
              <li>Parse JSON configuration files</li>
              <li>Generate config structs</li>
              <li>Validate config formats</li>
              <li>Create config templates</li>
            </ul>

            <h3>4. Testing and Documentation</h3>
            <ul>
              <li>Generate JSON data for testing</li>
              <li>Create API documentation examples</li>
              <li>Write mock data for unit tests</li>
              <li>Generate Swagger doc models</li>
            </ul>

            <h3>5. Data Conversion</h3>
            <ul>
              <li>Convert third-party API returns to Go types</li>
              <li>Unify formats from different data sources</li>
              <li>Refactor data structures in legacy code</li>
              <li>Type definitions during project migration</li>
            </ul>
          `
        },
        {
          title: 'Best Practices',
          content: `
            <h3>Code Conventions</h3>
            <ul>
              <li>Struct names use PascalCase (capitalized camel case)</li>
              <li>Field names also use PascalCase to ensure exportability</li>
              <li>Add meaningful comments explaining each field's purpose</li>
              <li>Put related structs in same file</li>
            </ul>

            <h3>Type Selection</h3>
            <ul>
              <li>Prefer int for integers, int64 for large numbers</li>
              <li>Consistently use float64 for floating point</li>
              <li>Use pointer types for optional fields (*string, *int, etc.)</li>
              <li>Use time.Time for time fields instead of string</li>
              <li>Consider custom types for enum values</li>
            </ul>

            <h3>Tag Usage</h3>
            <ul>
              <li>Don't use omitempty for required fields</li>
              <li>Add omitempty tag for optional fields</li>
              <li>Explicitly specify tags when different from JSON key names</li>
              <li>Use "-" tag to ignore fields that shouldn't be serialized</li>
            </ul>

            <h3>Structure Design</h3>
            <ul>
              <li>Avoid deep nesting, consider splitting beyond 3 levels</li>
              <li>Reuse common sub-structs</li>
              <li>Use composition over inheritance</li>
              <li>Split large structs into multiple smaller ones</li>
            </ul>

            <h3>Example Code</h3>
            <pre><code>package models

import "time"

// User represents user information
type User struct {
    ID        int64     \`json:"id"\`
    Name      string    \`json:"name"\`
    Email     string    \`json:"email"\`
    Age       *int      \`json:"age,omitempty"\`
    CreatedAt time.Time \`json:"created_at"\`
    Profile   *Profile  \`json:"profile,omitempty"\`
}

// Profile represents user profile
type Profile struct {
    Bio    string   \`json:"bio,omitempty"\`
    Avatar string   \`json:"avatar,omitempty"\`
    Tags   []string \`json:"tags,omitempty"\`
}</code></pre>
          `
        }
      ]
    }
  };

  const data = content[language];

  return (
    <div className="bg-[#faf9f6] dark:bg-gray-900">
      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="flex gap-8">
          {/* 主内容区 */}
          <article className="flex-1 min-w-0">
            {/* 页面标题 */}
            <div className="mb-12">
              <div className="flex items-center gap-3 mb-3">
                <FileCode size={28} className="text-emerald-600 dark:text-emerald-400" />
                <h1 className="text-3xl font-bold text-gray-900 dark:text-white">{data.title}</h1>
              </div>
              <p className="text-gray-600 dark:text-gray-400">{data.subtitle}</p>
            </div>

            <div className="prose prose-lg dark:prose-invert max-w-none
          prose-headings:font-bold prose-headings:text-gray-900 dark:prose-headings:text-white
          prose-h2:text-2xl prose-h2:mt-12 prose-h2:mb-6 prose-h2:pb-2 prose-h2:border-b prose-h2:border-gray-200 dark:prose-h2:border-gray-700
          prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-4
          prose-p:text-gray-700 dark:prose-p:text-gray-300 prose-p:leading-relaxed prose-p:mb-4
          prose-a:text-emerald-600 dark:prose-a:text-emerald-400 prose-a:no-underline hover:prose-a:underline
          prose-strong:text-gray-900 dark:prose-strong:text-white prose-strong:font-semibold
          prose-ul:my-4 prose-li:my-2
          prose-table:border-collapse prose-table:w-full
          prose-th:border prose-th:border-gray-300 dark:prose-th:border-gray-600 prose-th:bg-gray-100 dark:prose-th:bg-gray-800 prose-th:p-3
          prose-td:border prose-td:border-gray-300 dark:prose-td:border-gray-600 prose-td:p-3
          prose-code:text-emerald-600 dark:prose-code:text-emerald-400 prose-code:bg-gray-100 dark:prose-code:bg-gray-800 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded
          prose-pre:bg-gray-100 dark:prose-pre:bg-gray-800 prose-pre:p-4 prose-pre:rounded-lg
        ">
          {data.sections.map((section, index) => (
            <section key={index} id={`section-${index}`}>
              <h2>
                {section.title}
              </h2>

              {section.items && (
                <div className="grid md:grid-cols-2 gap-6 not-prose">
                  {section.items.map((item, i) => (
                    <div key={i} className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
                      <h4 className="text-lg font-bold text-gray-900 dark:text-white mb-2">{item.title}</h4>
                      <p className="text-gray-600 dark:text-gray-400">{item.desc}</p>
                    </div>
                  ))}
                </div>
              )}

              {section.content && (
                <div dangerouslySetInnerHTML={{ __html: section.content }} />
              )}

              {section.faqs && (
                <div className="space-y-6">
                  {section.faqs.map((faq, i) => (
                    <div key={i} className="bg-emerald-50 dark:bg-emerald-900/20 rounded-lg p-6 border border-emerald-200 dark:border-emerald-700">
                      <div className="flex items-start gap-3">
                        <HelpCircle size={20} className="text-emerald-600 dark:text-emerald-400 flex-shrink-0 mt-1" />
                        <div>
                          <h4 className="text-lg font-bold text-gray-900 dark:text-white mb-2">{faq.q}</h4>
                          <p className="text-gray-700 dark:text-gray-300 leading-relaxed">{faq.a}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>
          ))}
        </div>

        {/* Call to Action */}
        <div className="mt-12 bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 rounded-2xl p-8 border border-emerald-200 dark:border-emerald-700">
          <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
            {language === 'zh' ? '开始使用' : 'Get Started'}
          </h3>
          <p className="text-gray-700 dark:text-gray-300 mb-6">
            {language === 'zh'
              ? '立即体验 DevToolkit 的 Go 结构体转换工具，高效处理 JSON 和 Go 代码转换！'
              : 'Experience DevToolkit\'s Go struct converter now for efficient JSON and Go code conversion!'}
          </p>
          <div className="flex flex-wrap gap-4">
            <Link
              to="/gostruct"
              className="inline-flex items-center gap-2 px-6 py-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-medium transition-colors"
            >
              <FileCode size={20} />
              {language === 'zh' ? '打开 Go 结构体工具' : 'Open Go Struct Tool'}
            </Link>
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 px-6 py-3 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg border border-gray-300 dark:border-gray-600 font-medium transition-colors"
            >
              {language === 'zh' ? '查看更多教程' : 'More Tutorials'}
            </Link>
          </div>
        </div>

        {/* Back Button */}
        <div className="mt-12 pt-8 border-t border-gray-200 dark:border-gray-700">
          <Link
            to="/gostruct"
            className="inline-flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg border border-gray-300 dark:border-gray-600 transition-colors"
          >
            <ArrowLeft size={16} />
            {language === 'zh' ? '返回 Go 结构体工具' : 'Back to Go Struct Tool'}
          </Link>
        </div>
      </article>

      {/* 右侧大纲 */}
      <aside className="hidden lg:block w-64 flex-shrink-0">
        <div className="sticky top-20">
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
            <h2 className="text-sm font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <svg className="w-4 h-4 text-emerald-600 dark:text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h7" />
              </svg>
              {language === 'zh' ? '目录' : 'Contents'}
            </h2>
            <nav className="space-y-1">
              {data.sections.map((section, index) => (
                <a
                  key={index}
                  href={`#section-${index}`}
                  onClick={(e) => {
                    e.preventDefault();
                    document.getElementById(`section-${index}`)?.scrollIntoView({ behavior: 'smooth' });
                  }}
                  className={`block px-3 py-2 text-sm rounded-lg transition-colors ${
                    activeSection === index
                      ? 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 font-medium'
                      : 'text-gray-600 dark:text-gray-400 hover:text-emerald-600 dark:hover:text-emerald-400 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                  }`}
                >
                  <span className="flex items-center gap-2">
                    <span className={`flex-shrink-0 w-5 text-xs ${activeSection === index ? 'font-bold' : ''}`}>
                      {index + 1}.
                    </span>
                    <span className="line-clamp-2">{section.title}</span>
                  </span>
                </a>
              ))}
            </nav>
          </div>
        </div>
      </aside>
    </div>
      </div>
    </div>
  );
}

export default GoStructHelp;
